import { CHIMNEY_CLEARANCE_TO_COMBUSTIBLES_MM } from "./calculateHeaterPlacement";
import { CHIMNEY_OUTER_DIAMETER_MM } from "./chimneyStack";

/**
 * Skorstensväxling: the trimmed opening a flue needs where it cannot pass
 * between the members of a framing layer with
 * `CHIMNEY_CLEARANCE_TO_COMBUSTIBLES_MM` to spare.
 *
 * The bay resolvers (`calculateHeaterPlacement`, `dividerHeaterCenterZM`) move
 * the stove so that its flue lands in a joist bay where they can. Where they
 * cannot — a bay narrower than Ø280 + 2 × 100 mm, a stove the user placed, a
 * slide capped by the bench — the members the flue would hit are cut short and
 * their ends carried on two headers (växlingar) across to the nearest uncut
 * members. One plan per layer, used by the 3D model AND the BOM, so the model
 * never draws an opening the list does not buy.
 *
 * A layer is a row of parallel members: the roof joists (span along X, spread
 * along Z) and the ceiling takgles (run along Z, spread along X). The plan is
 * stated in the layer's own axes — `along` is the members' run, `across` the
 * direction they are spread in.
 */

/** Ø280 pipe radius plus the 100 mm clearance: no combustible face inside it. */
export const CHIMNEY_KEEP_OUT_RADIUS_M =
	(CHIMNEY_OUTER_DIAMETER_MM / 2 + CHIMNEY_CLEARANCE_TO_COMBUSTIBLES_MM) / 1000;

export interface MemberRowInput {
	/** Member centre lines, ascending, in the `across` axis. */
	positionsM: readonly number[];
	/** Member width in the `across` axis. */
	memberWidthM: number;
	/** Flue axis in the layer's (`along`, `across`) axes. */
	flueAlongM: number;
	flueAcrossM: number;
	/**
	 * Where a header may end when no uncut member stands on that side (the
	 * hammarband / wall line). Absent = the outermost members are uncuttable,
	 * as the gable trusses are.
	 */
	acrossLimitsM?: readonly [number, number];
	keepOutRadiusM?: number;
}

export interface TrimmerHeader {
	/** Header centre line in the `along` axis. */
	alongM: number;
	/** Clear span between the members it is fixed to, in the `across` axis. */
	fromAcrossM: number;
	toAcrossM: number;
}

export interface MemberRowTrim {
	/** Indices (into `positionsM`) of the members cut short around the flue. */
	cutIndices: number[];
	/** The stretch of `along` the cut members leave open: keep-out plus a header. */
	gapAlongM: readonly [number, number];
	/** Two headers, one either side of the flue, both outside the keep-out. */
	headers: [TrimmerHeader, TrimmerHeader];
}

/**
 * Null when the flue already clears every member of the row.
 *
 * Without `acrossLimitsM` the outermost members are never cut (a gable truss
 * carries the end of the roof): the headers stop at them, and a flue close
 * enough to touch one is left touching it. The scene sweep in
 * `chimneyFramingClearance.test.ts` is what reports that, not a throw in the
 * render path.
 */
export function planMemberRowTrim(input: MemberRowInput): MemberRowTrim | null {
	const keepOut = input.keepOutRadiusM ?? CHIMNEY_KEEP_OUT_RADIUS_M;
	const half = input.memberWidthM / 2;
	const reach = keepOut + half;
	const positions = input.positionsM;
	const limits = input.acrossLimitsM;
	const cuttable = (index: number) =>
		limits !== undefined || (index > 0 && index < positions.length - 1);
	const cutIndices = positions.flatMap((p, index) =>
		Math.abs(p - input.flueAcrossM) < reach - 1e-9 && cuttable(index)
			? [index]
			: [],
	);
	if (cutIndices.length === 0) return null;

	const first = cutIndices[0];
	const last = cutIndices[cutIndices.length - 1];
	const fromAcrossM =
		first > 0
			? positions[first - 1] + half
			: (limits as readonly [number, number])[0];
	const toAcrossM =
		last < positions.length - 1
			? positions[last + 1] - half
			: (limits as readonly [number, number])[1];
	// The header's inner face sits ON the keep-out line, so it keeps exactly
	// the clearance. The cut members butt into its outer face, so they stop a
	// whole member width further out — never inside the header.
	const headerOffset = keepOut + half;
	const gapHalf = keepOut + input.memberWidthM;
	return {
		cutIndices,
		gapAlongM: [input.flueAlongM - gapHalf, input.flueAlongM + gapHalf],
		headers: [
			{ alongM: input.flueAlongM - headerOffset, fromAcrossM, toAcrossM },
			{ alongM: input.flueAlongM + headerOffset, fromAcrossM, toAcrossM },
		],
	};
}

/**
 * The pieces of a member running `fromM → toM` that remain once `gap` is
 * taken out, as [start, end] pairs. A member the gap misses comes back whole.
 */
export function memberPiecesAround(
	fromM: number,
	toM: number,
	gap: readonly [number, number] | null,
): [number, number][] {
	if (!gap || gap[1] <= fromM || gap[0] >= toM) return [[fromM, toM]];
	const pieces: [number, number][] = [];
	if (gap[0] - fromM > 1e-6) pieces.push([fromM, gap[0]]);
	if (toM - gap[1] > 1e-6) pieces.push([gap[1], toM]);
	return pieces;
}
